import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { resumeData } from './data/resumeData';
import { portfolioData } from './data/portfolioData';
import ResumeHeader from './components/ResumeHeader';
import ResumeSummary from './components/ResumeSummary';
import ResumeSkills from './components/ResumeSkills';
import ResumeExperience from './components/ResumeExperience';
import ResumeEducation from './components/ResumeEducation';
import Portfolio from './components/Portfolio';
import SafeIcon from './common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiDownload, FiFileText, FiBriefcase, FiUser, FiFile } = FiIcons;

function App() {
  const [activeTab, setActiveTab] = useState('resume');
  const [pdfLoading, setPdfLoading] = useState(false);
  const [docxLoading, setDocxLoading] = useState(false);

  const handleDownloadPDF = async () => {
    setPdfLoading(true);
    try {
      const { pdf } = await import('@react-pdf/renderer');
      const { default: ResumePDF } = await import('./components/ResumePDF');
      const blob = await pdf(<ResumePDF data={resumeData} />).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Sean_R_Resume.pdf';
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setPdfLoading(false);
    }
  };

  const handleDownloadDocx = async () => {
    setDocxLoading(true);
    try {
      const { generateDocx } = await import('./utils/generateDocx');
      const blob = await generateDocx(resumeData);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Sean_Roumaya_Resume.docx';
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDocxLoading(false);
    }
  };

  const handleDownloadCSV = () => {
    const { personal, summary, skills, experience, additionalExperience, education } = resumeData;

    const allSkills = skills.flatMap(g => g.items).join('; ');

    const rows = [
      ['Field', 'Value'],
      ['First Name', personal.firstName],
      ['Last Name', personal.lastName],
      ['Full Name', `${personal.firstName} ${personal.lastName}`],
      ['Title / Headline', personal.title],
      ['Email', personal.email],
      ['Phone Number', personal.phone],
      ['Address Line 1', personal.addressLine1],
      ['City', personal.city],
      ['State', personal.state],
      ['Postal Code', personal.zip],
      ['Country', 'US'],
      ['Summary', summary],
      ['Skills', allSkills],
      [],
      ['--- WORK EXPERIENCE ---'],
      ['Job Title', 'Company', 'Location', 'Dates', 'Description'],
      ...experience.map(job => [
        job.role,
        job.company,
        job.location,
        job.period,
        job.highlights.join(' | '),
      ]),
      ...additionalExperience.map(job => [
        job.role,
        job.company,
        job.location,
        job.period,
        job.bullet,
      ]),
      [],
      ['--- EDUCATION ---'],
      ['Degree', 'School', 'Location'],
      [education.degree, education.school, education.location],
    ];

    const csv = rows
      .map(row => row.map(cell => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\r\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Sean_Roumaya_Resume.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4 sm:px-6 lg:px-8 font-sans print:bg-white print:py-0 print:px-0">
      {/* Navigation & Actions */}
      <div className="max-w-4xl mx-auto mb-8 flex flex-col sm:flex-row justify-between items-center gap-4 print:hidden">
        <div className="bg-white p-1 rounded-xl shadow-sm border border-slate-200 flex">
          <button
            onClick={() => setActiveTab('resume')}
            className={`flex items-center gap-2 px-6 py-2 rounded-lg font-bold transition-all ${
              activeTab === 'resume'
                ? 'bg-blue-600 text-white shadow-md'
                : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            <SafeIcon icon={FiUser} />
            <span>Resume</span>
          </button>
          <button
            onClick={() => setActiveTab('portfolio')}
            className={`flex items-center gap-2 px-6 py-2 rounded-lg font-bold transition-all ${
              activeTab === 'portfolio'
                ? 'bg-blue-600 text-white shadow-md'
                : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            <SafeIcon icon={FiBriefcase} />
            <span>Portfolio</span>
          </button>
        </div>

        <div className="flex flex-wrap gap-3 justify-end">
          <button
            onClick={handleDownloadDocx}
            disabled={docxLoading}
            className="flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white px-6 py-2.5 rounded-xl shadow-lg transition-all font-bold group active:scale-95 disabled:opacity-60 disabled:cursor-wait"
          >
            <SafeIcon icon={FiFile} className="group-hover:animate-bounce" />
            <span>{docxLoading ? 'Generating...' : 'Download DOCX'}</span>
          </button>
          <button
            onClick={handleDownloadPDF}
            disabled={pdfLoading}
            className="flex items-center gap-2 bg-slate-900 hover:bg-black text-white px-6 py-2.5 rounded-xl shadow-lg transition-all font-bold group active:scale-95 disabled:opacity-60 disabled:cursor-wait"
          >
            <SafeIcon icon={FiDownload} className="group-hover:animate-bounce" />
            <span>{pdfLoading ? 'Generating...' : 'Download PDF'}</span>
          </button>
          <button
            onClick={handleDownloadCSV}
            className="flex items-center gap-2 bg-slate-600 hover:bg-slate-700 text-white px-6 py-2.5 rounded-xl shadow-lg transition-all font-bold group active:scale-95"
          >
            <SafeIcon icon={FiFileText} className="group-hover:animate-bounce" />
            <span>Download CSV</span>
          </button>
        </div>
      </div>

      {/* Main Container */}
      <motion.div
        layout
        className="max-w-4xl mx-auto bg-white shadow-2xl rounded-2xl overflow-hidden print:shadow-none print:rounded-none"
      >
        <div className="p-8 sm:p-12 md:p-16 print:p-0">
          <AnimatePresence mode="wait">
            {activeTab === 'resume' ? (
              <motion.div
                key="resume"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.3 }}
              >
                <ResumeHeader personal={resumeData.personal} />
                <ResumeSummary summary={resumeData.summary} />
                <ResumeSkills skills={resumeData.skills} />
                <ResumeExperience
                  experience={resumeData.experience}
                  additionalExperience={resumeData.additionalExperience}
                />
                <ResumeEducation education={resumeData.education} />
              </motion.div>
            ) : (
              <motion.div
                key="portfolio"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <div className="mb-10">
                  <h2 className="text-3xl font-bold text-slate-900 mb-2">Website Showcase</h2>
                  <p className="text-slate-600">A collection of AI assisted website design projects.</p>
                </div>
                <Portfolio projects={portfolioData} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>

      {/* Optimized Print Styles for Indeed/ATS CSV Parsing */}
      <style>{`
        @media print {
          @page { margin: 0.5in; size: letter; }
          body { 
            background: white !important; 
            color: black !important; 
            -webkit-print-color-adjust: exact; 
          }
          .print\\:hidden { display: none !important; }
          
          /* Force text to black for better contrast in PDF */
          h1, h2, h3, h4, p, li, span, div { color: black !important; }
          h1, h2, h3, h4 { page-break-after: avoid; }
          
          /* FIX FOR THE GAPS: Allow sections to break naturally across pages */
          section { page-break-inside: auto; margin-bottom: 20px !important; }
          
          /* Prevent individual items (like a single job or skill card) from splitting in half */
          .experience-item, .skill-group { page-break-inside: avoid; }
          
          /* Ensure links are readable but not intrusive */
          a { text-decoration: none; color: black !important; }
          
          /* Clean up shadows and backgrounds for better scanning */
          .bg-slate-50, .bg-blue-50, .bg-slate-100 { 
            background-color: transparent !important; 
            border: 1px solid #ddd !important; 
          }
          .border-slate-200 { border-color: #ddd !important; }
          .rounded-xl, .rounded-2xl { border-radius: 4px !important; }
        }
      `}</style>
    </div>
  );
}

export default App;